"use client";

import { type ComponentProps } from "react";

import { cn } from "@oneleet/utils";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { type VariantProps, cva } from "class-variance-authority";

const dotVariants = cva(
  "ring-neutral-250 group-data-[state=checked]:bg-accent-800 group-data-[disabled]:bg-neutral-250 group-data-[state=checked]:group-data-[disabled]:bg-neutral-250 flex shrink-0 items-center justify-center rounded-full ring-[1.4px] ring-inset focus-visible:shadow-[0px_0px_0px_1px_theme(colors.accent.800),_0px_0px_0px_4px_theme(colors.accent.200),_0px_1px_3px_-1px_rgba(0,_0,_0,_0.05),_0px_1px_3px_0px_rgba(0,_0,_0,_0.10)] focus-visible:outline-none group-data-[disabled]:cursor-not-allowed group-data-[state=checked]:group-data-[disabled]:ring-transparent group-data-[state=checked]:ring-transparent",
  {
    variants: {
      size: {
        sm: "size-3.5",
        md: "size-4",
        lg: "size-5",
      },
    },
  },
);

const selectedDotIndicatorVariants = cva(
  "invisible size-1.5 rounded-full bg-white group-data-[state=checked]:visible group-data-[disabled]:bg-neutral-600",
  {
    variants: {
      size: {
        sm: "",
        md: "",
        lg: "size-2",
      },
    },
  },
);

function NgRadioGroup({ ...props }: RadioGroupPrimitive.RadioGroupProps) {
  return <RadioGroupPrimitive.Root {...props} />;
}
NgRadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

function NgRadioItem({
  className,
  ...props
}: RadioGroupPrimitive.RadioGroupItemProps) {
  return (
    <RadioGroupPrimitive.Item
      className={cn("group data-[disabled]:cursor-not-allowed", className)}
      {...props}
    />
  );
}
NgRadioItem.displayName = RadioGroupPrimitive.Item.displayName;

function NgRadioDot({
  className,
  size = "md",
  ...props
}: ComponentProps<"div"> & VariantProps<typeof dotVariants>) {
  return (
    <div className={cn(dotVariants({ size, className }))} {...props}>
      <div className={cn(selectedDotIndicatorVariants({ size }))} />
    </div>
  );
}
NgRadioDot.displayName = "NgRadioDot";

const NgRadioComponent = Object.assign(
  {},
  {
    Group: NgRadioGroup,
    Item: NgRadioItem,
    Dot: NgRadioDot,
  },
);

export { NgRadioComponent as NgRadio };
